
import { useEffect, useState } from "react";
import axios from 'axios';
import { Modal, Button, Form } from "react-bootstrap";
import AdminDashboardLayout from "../../../Components/AdminDashboard/AdminDashboardLayout";
import Loading from 'react-fullscreen-loading';
import { toast } from "react-hot-toast";
import DataTable from 'react-data-table-component';
import ShowTime from "../../../utils/ShowTime/ShowTime";
import { FaEdit, FaTrash } from "react-icons/fa";
import './index.css';
import TagList from "../../../Components/TagDisplay/TagList";

import TagModal from "../../../Components/Tags/TagModal";
import { Can, useAuth } from "../../../authentication/AuthContext";
import { TruncatedTextTooltip, DateCell, COLUMN_WIDTHS } from "../../../Components/TruncatedText/TruncatedText";

export default function TagsIndex() {
  const { can } = useAuth();
    const [screenloader, setScreenloader] = useState(true);
    const [TableData, setTableData] = useState([]);
    const [TotalRecords, setTotalRecords] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [pending, setPending] = useState(true);

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [selectedTag, setSelectedTag] = useState(null);

    const getList = async () => {
        try {
            const filters = [];
            if (search) {
                filters.push({ column: 'name', operator: 'LIKE', value: `%${search}%` });
            }

            const formattedFilters = filters
                .map((filter, index) => ({
                    [`filters[${index}][column]`]: filter.column,
                    [`filters[${index}][operator]`]: filter.operator,
                    [`filters[${index}][value]`]: filter.value,
                }))
                .reduce((acc, obj) => ({ ...acc, ...obj }), {});

            const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/tags`, {
                params: {
                    limit: pageSize,
                    page: page,
                    ...formattedFilters
                },
                headers: {
                    'access-token': localStorage.getItem('accessToken'),
                },
            });

            setScreenloader(false);
            setPending(false);
            if (response.data.status) {
                setTableData(response.data.tags);
                setTotalRecords(response.data.meta.pagination.total);
            } else {
                toast.error('Something Went Wrong');
            }
        } catch (error) {
            setScreenloader(false);
            setPending(false);
            toast.error(error.response?.data?.message || 'Something Went Wrong');
        }
    };

    useEffect(() => {
        setPending(true);
        getList();
    }, [page, pageSize, search]);

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this tag?")) return;
        setScreenloader(true);
        try {
            const res = await axios.delete(`${process.env.REACT_APP_API_URL}/admin/tags/${id}`, {
                headers: { 'access-token': localStorage.getItem('accessToken') }
            });
            if (res.data.status) {
                toast.success("Tag deleted successfully");
                getList();
            } else {
                toast.error(res.data.message || "Delete failed");
                setScreenloader(false);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Delete failed");
            setScreenloader(false);
        }
    }

    const openModal = (tag = null) => {
        setSelectedTag(tag);
        setShowModal(true);
    }

    const columns = [
        {
            name: "Id",
            selector: row => row.id,
            cell: row => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span>{row.id}</span>
                </div>
            ),
            sortable: true
        },
        {
            name: "Name",
            selector: row => row.name,
            cell: row => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <TagList tags={[row]} />
                    <span>{row.name}</span>
                </div>
            ),
            sortable: true
        },
        {
            name: "Color",
            selector: row => row.color,
            cell: row => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div className="color-swatch" style={{ backgroundColor: row.color }}></div>
                    <span>{row.color}</span>
                </div>
            )
        },
        {
            name: "Created At",
            minWidth: `${COLUMN_WIDTHS.date}px`,
            selector: row => row.created_at,
            cell: row => <DateCell value={ShowTime(row.created_at)} />,
            sortable: true
        },
        {
            name: "Actions",
            // Hidden entirely rather than left as an empty column: omit drops the
            // header as well as the cells.
            omit: !(can(['admin.tags.update', 'admin.tags.delete'])),
            cell: row => (
                <div className="tags-table-actions">
                    <Can permission="admin.tags.update">
                        <FaEdit className="action-icon edit-icon" onClick={() => openModal(row)} />
                    </Can>
                    <Can permission="admin.tags.delete">
                        <FaTrash className="action-icon delete-icon" onClick={() => handleDelete(row.id)} />
                    </Can>
                </div>
            )
        }
    ];

    return (
        <div>
            <AdminDashboardLayout />
            <Loading loading={screenloader} background="#fff" loaderColor="#883484" />
            <div className='content-page-bg'>
                <div className='content-page'>
                    <div className='content'>
                        <div className="row align-items-center mb-3">
                            <div className="col-12 col-md-10 mb-2 mb-md-0">
                                <h2 className="m-0">Tags</h2>
                            </div>

                            <div className="col-12 col-md-2 d-flex justify-content-md-end">
                                <Can permission="admin.tags.create">
                                <Button
                                    className="admin-btn w-100 w-md-auto"
                                    onClick={() => openModal(null)}
                                >
                                    + New Tag
                                </Button>
                                </Can>
                            </div>
                        </div>

                        <div className="filters row mb-3">
                            <div className="col-md-4">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by name..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-12">
                                <DataTable
                                    columns={columns}
                                    data={TableData}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={TotalRecords}
                                    onChangePage={page => setPage(page)}
                                    onChangeRowsPerPage={newPerPage => setPageSize(newPerPage)}
                                    progressPending={pending}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <TagModal
                show={showModal}
                onHide={() => setShowModal(false)}
                tag={selectedTag}
                onSuccess={getList}
            />

            <TruncatedTextTooltip />
        </div>
    );
}
